import {
	AfterContentInit,
	Component,
	ContentChildren,
	ElementRef,
	EventEmitter,
	Input,
	Output,
	QueryList,
	TemplateRef,
	ViewChild
} from "@angular/core";
import { AutomataTemplateDirective } from "@shared/directives/automata-template.directive";

interface PendingSelection {
	[key: string]: boolean;
}

export interface GenericValue {
	id: number;
}

@Component({
	selector: "app-pick-list",
	templateUrl: "./pick-list.component.html",
	styleUrls: ["./pick-list.component.scss"],
	standalone: false
})
export class PickListComponent implements AfterContentInit {
	@ContentChildren(AutomataTemplateDirective)
	templates!: QueryList<AutomataTemplateDirective>;
	pickListItem!: TemplateRef<unknown>;

	@Input() unselectedList: GenericValue[] = [];
	@Input() selectedList: GenericValue[] = [];

	@Input() selectedListLabel = "";
	@Input() unselectedListLabel = "";

	@Input() disabled = false;
	@Input() itemsToDisable: number[] = [];

	@Output() selectedListEvent = new EventEmitter<GenericValue[]>();
	@Output() unselectedListEvent = new EventEmitter<GenericValue[]>();

	@ViewChild("selectedListElement") selectedListElement!: ElementRef;

	pendingSelection: PendingSelection = {};

	ngAfterContentInit(): void {
		this.templates.forEach((item) => {
			switch (item.getType()) {
				case "item":
					this.pickListItem = item.template;
					break;
				default:
					break;
			}
		});
	}

	togglePendingSelection(value: GenericValue): void {
		if (this.disabled || this.isItemDisabled(value.id)) {
			return;
		}

		this.pendingSelection[value.id] = !this.pendingSelection[value.id];
	}

	addToSelectedList(value?: GenericValue): void {
		if (this.disabled || (value && this.isItemDisabled(value.id))) {
			return;
		}

		const newselectedList = value
			? [value]
			: this.getPendingSelection(this.unselectedList);
		this.pendingSelection = {};

		this.unselectedList = this.removeValueFromList(
			this.unselectedList,
			newselectedList
		);

		this.selectedList = newselectedList.concat(this.selectedList);
		(<HTMLElement>this.selectedListElement.nativeElement).scroll(0, 0);

		this.selectedListEvent.emit(this.selectedList);
		this.unselectedListEvent.emit(this.unselectedList);
	}

	addAllToSelectedList(): void {
		const allItems = this.unselectedList.concat(this.selectedList);
		this.selectedList = allItems.filter(
			(item) => !this.itemsToDisable.includes(item.id)
		);
		const itemsToNotMove = allItems.filter((item) =>
			this.itemsToDisable.includes(item.id)
		);
		(<HTMLElement>this.selectedListElement.nativeElement).scroll(0, 0);
		this.unselectedList = [...itemsToNotMove];
		this.pendingSelection = {};
		this.selectedListEvent.emit(this.selectedList);
		this.unselectedListEvent.emit(this.unselectedList);
	}

	removeFromselectedList(value?: GenericValue): void {
		if (this.disabled || (value && this.isItemDisabled(value.id))) {
			return;
		}

		const removedValues = value
			? [value]
			: this.getPendingSelection(this.selectedList);
		this.pendingSelection = {};

		this.selectedList = this.removeValueFromList(
			this.selectedList,
			removedValues
		);

		this.unselectedList = removedValues.concat(this.unselectedList);
		this.selectedListEvent.emit(this.selectedList);
		this.unselectedListEvent.emit(this.unselectedList);
	}

	removeAllFromselectedList(): void {
		this.unselectedList = this.selectedList.concat(this.unselectedList);

		this.selectedList = [];
		this.pendingSelection = {};

		this.selectedListEvent.emit(this.selectedList);
		this.unselectedListEvent.emit(this.unselectedList);
	}

	private getPendingSelection(values: GenericValue[]): GenericValue[] {
		return values.filter(
			(value) => this.pendingSelection[value.id] === true
		);
	}

	private removeValueFromList(
		values: GenericValue[],
		valuesToRemove: GenericValue[]
	): GenericValue[] {
		return values.filter((value) => !valuesToRemove.includes(value));
	}

	private isItemDisabled(id: number): boolean {
		return this.itemsToDisable.includes(id);
	}
}
